import { fetchWithToken, getApigeeUrl } from '@adsk/acs-sc-web-platform-api';

import {
  PAGE_SIZE,
  VERSION_1,
  VERSION_2,
  CUSTOM_ATTRIBUTE_OFFSET,
  CUSTOM_ATTRIBUTE_LIMIT,
  CUSTOM_ATTRIBUTES_URL_IDENTIFIER,
} from '../consts';
import {
  SubmittalsItemRawData,
  SubmittalsListResponse,
  CustomAttributesResponse,
  SubmittalsItem,
  CustomAttributesData,
  Spec,
  SpecRawData,
  SpecsResponse,
  Package,
} from '../types';
import { chunk } from '../utils/array';
import { getConfig } from '../utils/env';
import { urlParams } from '../utils/navigator';

interface Filters {
  searchExpression?: string;

  // filters
  assignedTo?: string[];
  specID?: string[];
  statusId?: string[];
  id?: string[];

  [key: string]: unknown;
}

interface SortOptions {
  field?: string;
  order?: string;
  columnName?: string;

  [key: string]: unknown;
}

interface QueryEntitiesOptions {
  projectId: string;
  pageLimit: number;
  filters: Filters;
  nextUrl?: string;
  sort?: SortOptions;
}

const DEFAULT_SORT_FIELD = 'identifier';

const ITEM_SORT_FIELD = 'updatedAt';
const ITEM_SORT_ORDER = 'desc';
const ITEM_SORT = `${ITEM_SORT_FIELD} ${ITEM_SORT_ORDER}`;

function getItemsUrl(
  projectId: string,
  pageLimit: number,
  filters: Filters,
  sort?: SortOptions,
): string {
  const { searchExpression } = filters;
  const urlSearchParams = new URLSearchParams();
  urlSearchParams.append('limit', `${pageLimit}`);
  if (searchExpression) {
    urlSearchParams.append('search', searchExpression);
  }

  Object.entries(filters)
    .filter(([key]) => key !== 'searchExpression')
    .map(([key, value]) => ({
      filterName: `filter[${key}]`,
      value: Array.isArray(value) ? value.join(',') : value,
    }))
    .forEach(
      ({ filterName, value }) =>
        value && urlSearchParams.append(filterName, value as string),
    );

  if (sort) {
    urlSearchParams.append(
      'sort',
      sort.field && sort.order ? `${sort.field} ${sort.order}` : ITEM_SORT,
    );
  }

  return `${getApigeeUrl()}/${getConfig('listItemsPath')
    .replace(urlParams.projectId, projectId)
    .replace(urlParams.version, VERSION_2)}&${urlSearchParams.toString()}`;
}

interface SubmittalsItemDataAggregated extends Omit<
  SubmittalsItemRawData,
  | 'specId'
  | 'specIdentifier'
  | 'specTitle'
  | 'packageId'
  | 'packageTitle'
  | 'packageIdentifier'
  | 'packageSpecIdentifier'
> {
  spec: Spec;
  package: Package;
}
function aggregateDataFields(
  item: SubmittalsItemRawData,
): SubmittalsItemDataAggregated {
  return {
    ...item,
    spec: {
      id: item.specId,
      identifier: item.specIdentifier,
      title: item.specTitle,
    },
    package: {
      id: item?.packageId || '',
      identifier: item?.packageIdentifier || 0,
      specIdentifier: item?.packageSpecIdentifier || '',
      title: item?.packageTitle || '-',
    },
  };
}

export const getAbsoluteDateFromString = (str: string) => {
  const [year, month, day] = str.split('-').map(Number);
  //month is calculated from index 0 like arrays, so we need -1 here.
  return new Date(Number(year), Number(month) - 1, Number(day), 0, 0, 0, 0);
};

function convertDates(item: SubmittalsItemDataAggregated): SubmittalsItem {
  return {
    ...item,
    dueDate: item.dueDate && getAbsoluteDateFromString(item.dueDate),
    updatedAt: item.updatedAt && getAbsoluteDateFromString(item.updatedAt),
  } as unknown as SubmittalsItem;
}

export async function queryEntities({
  projectId,
  filters,
  nextUrl,
  sort,
}: QueryEntitiesOptions): Promise<{
  data: SubmittalsItem[];
  nextPageQuery?: QueryEntitiesOptions;
}> {
  const url = nextUrl || getItemsUrl(projectId, PAGE_SIZE, filters, sort);
  return fetchWithToken(url).then((result) => ({
    data: (result as SubmittalsListResponse).results
      .map(aggregateDataFields)
      .map(convertDates),
    nextPageQuery: (result as SubmittalsListResponse).pagination.nextUrl
      ? {
          projectId,
          pageLimit: PAGE_SIZE,
          filters,
          nextUrl: (result as SubmittalsListResponse).pagination.nextUrl,
        }
      : undefined,
  }));
}

export async function getEntitiesByIds(
  projectId: string,
  ids: string[],
): Promise<{ data: SubmittalsItem[] }> {
  const idChunks = chunk(ids, 20) as string[][];
  return Promise.all(
    idChunks.map((ids) =>
      queryEntities({
        projectId,
        pageLimit: PAGE_SIZE,
        filters: { id: ids },
      }),
    ),
  )
    .then((results) => results.map((response) => response.data))
    .then((results) => results.flat())
    .then((result) => ({ data: result }));
}

export async function getSpecs(
  projectId: string,
  isCustomReviewEnabled: boolean,
): Promise<SpecRawData[]> {
  const version = isCustomReviewEnabled ? VERSION_2 : VERSION_1;
  const url = `${getApigeeUrl()}/${getConfig('listSpecsPath')
    .replace(urlParams.projectId, projectId)
    .replace(
      urlParams.version,
      version,
    )}?limit=1000&sort=${DEFAULT_SORT_FIELD}`;

  let aggregatedData: SpecRawData[] = [];

  async function aggregateSpecs(url: string): Promise<SpecRawData[]> {
    return fetchWithToken<SpecsResponse>(url).then(
      ({ results, pagination }) => {
        aggregatedData = [...aggregatedData, ...results];

        if (pagination.nextUrl) {
          return aggregateSpecs(pagination.nextUrl);
        } else {
          return aggregatedData;
        }
      },
    );
  }

  return aggregateSpecs(url);
}

export async function getCustomAttributes(
  projectId: string,
): Promise<CustomAttributesResponse> {
  const version = VERSION_2;
  const baseUrl = `${getApigeeUrl()}/${getConfig(
    CUSTOM_ATTRIBUTES_URL_IDENTIFIER,
  )
    .replace(urlParams.projectId, projectId)
    .replace(urlParams.version, version)}`;

  let aggregatedData: CustomAttributesData[] = [];

  async function aggregateCustomAttributes(
    offset: number = CUSTOM_ATTRIBUTE_OFFSET,
    limit: number = CUSTOM_ATTRIBUTE_LIMIT,
  ): Promise<CustomAttributesData[]> {
    const url = `${baseUrl}?limit=${limit}&offset=${offset}`;
    return fetchWithToken<CustomAttributesResponse>(url).then(
      ({ results, pagination }) => {
        aggregatedData = [...aggregatedData, ...results];
        const hasNextPage =
          pagination && pagination.totalResults > offset + results.length;
        if (hasNextPage) {
          return aggregateCustomAttributes(offset + limit, limit);
        } else {
          return aggregatedData;
        }
      },
    );
  }

  const results = await aggregateCustomAttributes();

  return {
    results,
    pagination: {
      limit: results.length,
      offset: 0,
      totalResults: results.length,
    },
  } as CustomAttributesResponse;
}
